/**
 * LES BANDES DESSINEES DE LA BIBLIOTHEQUE.
 *
 * ECRITES A LA MAIN, pas engendrees. Le moteur de l'ecole sait fabriquer des
 * phrases justes par gabarits (shared/sim/ecole/), et on aurait pu s'en servir
 * ici — mais une histoire n'est pas un exercice: engendrer les cases aurait
 * donne des BD infinies qui se lisent toutes pareil, et un enfant referme un
 * livre qui ne raconte rien. Ce sont donc des textes ecrits, courts, et
 * relus par `tests/unit/bd.test.ts`.
 *
 * LES DESSINS PASSENT PAR LE MEME FORMAT QUE LE MUSEE (`Trait`, dans
 * shared/sim/dessin.ts): meme grille de 0 a 1000, meme fonction de trace cote
 * client. Il n'y a donc AUCUN fichier d'image dans le jeu — la regle du projet,
 * qui n'a pas une seule texture — et une case se relit sur un telephone comme
 * sur un ecran de bureau.
 *
 * Les scenes sont composees a partir d'un petit vocabulaire de formes
 * (`sol`, `arbre`, `lapin`, `pluie`...). C'est ce qui rend une case lisible en
 * quatre lignes au lieu de cent coordonnees: quand une forme doit changer, elle
 * change partout ou elle apparait, et les douze cases restent d'accord entre
 * elles.
 *
 * Ce module est PUR et ne contient que des donnees: ni DOM, ni canvas, ni
 * hasard. Les chaines affichees portent leurs accents, les identifiants n'en
 * ont pas — la separation habituelle du projet.
 */
import type { Trait } from '../sim/dessin.ts'

// ------------------------------------------------------------- vocabulaire

const NOIR = 0x1a1a1a
const VERT = 0x6fbf4a
const VERT_FONCE = 0x2f7d3a
const BRUN = 0x9a6b3f
const BLEU = 0x3f7fd4
const BLEU_PALE = 0x8fc4ea
const JAUNE = 0xf5d13a
const ROUGE = 0xe03c3c
const GRIS = 0xb8bec4
const ROSE = 0xd45fa8
const BLANC_VOILE = 0xffffff

/** Un trait libre: la brique de tout ce qui suit. */
function t(couleur: number, epaisseur: number, ...points: number[]): Trait {
  return { couleur, epaisseur, points }
}

/** Un cercle approche par un polygone: assez rond a l'ecran, exact a l'ecriture. */
function cercle(couleur: number, epaisseur: number, cx: number, cy: number, r: number, cotes = 16): Trait {
  const points: number[] = []
  for (let i = 0; i <= cotes; i++) {
    const a = (i / cotes) * Math.PI * 2
    points.push(Math.round(cx + Math.cos(a) * r), Math.round(cy + Math.sin(a) * r))
  }
  return { couleur, epaisseur, points }
}

/** La ligne de sol, toujours a la meme hauteur: les cases se suivent. */
const HAUTEUR_SOL = 760

function sol(couleur = VERT): Trait[] {
  return [t(couleur, 26, 40, HAUTEUR_SOL, 960, HAUTEUR_SOL)]
}

function soleil(cx = 850, cy = 160): Trait[] {
  const rayons: Trait[] = []
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2
    rayons.push(
      t(
        JAUNE,
        10,
        Math.round(cx + Math.cos(a) * 78),
        Math.round(cy + Math.sin(a) * 78),
        Math.round(cx + Math.cos(a) * 112),
        Math.round(cy + Math.sin(a) * 112),
      ),
    )
  }
  return [cercle(JAUNE, 14, cx, cy, 62), ...rayons]
}

function nuage(cx: number, cy: number, ech = 1): Trait[] {
  return [
    cercle(GRIS, 12, cx - 55 * ech, cy, 42 * ech, 12),
    cercle(GRIS, 12, cx, cy - 22 * ech, 54 * ech, 12),
    cercle(GRIS, 12, cx + 60 * ech, cy, 40 * ech, 12),
  ]
}

function pluie(cx: number, cy: number): Trait[] {
  const gouttes: Trait[] = []
  for (let i = 0; i < 6; i++) {
    const x = cx - 90 + i * 36
    const y = cy + (i % 2) * 26
    gouttes.push(t(BLEU_PALE, 9, x, y, x - 10, y + 46))
  }
  return gouttes
}

function arbre(cx: number, echelle = 1): Trait[] {
  const h = 200 * echelle
  const yFeuillage = HAUTEUR_SOL - h - 60 * echelle
  return [
    t(BRUN, 22, cx, HAUTEUR_SOL, cx, HAUTEUR_SOL - h),
    // Une branche: un tronc nu se lisait comme un poteau.
    t(BRUN, 12, cx, Math.round(HAUTEUR_SOL - h * 0.55), Math.round(cx + 48 * echelle), Math.round(HAUTEUR_SOL - h * 0.8)),
    cercle(VERT_FONCE, 14, cx, yFeuillage, 90 * echelle),
    cercle(VERT, 12, cx - 55 * echelle, HAUTEUR_SOL - h - 20 * echelle, 55 * echelle, 12),
    cercle(VERT, 12, cx + 55 * echelle, HAUTEUR_SOL - h - 20 * echelle, 55 * echelle, 12),
    // Trois fruits rouges dans le feuillage: l'arbre est vivant, pas un decor.
    cercle(ROUGE, 9, cx - 34 * echelle, yFeuillage + 18 * echelle, 11 * echelle, 8),
    cercle(ROUGE, 9, cx + 40 * echelle, yFeuillage - 10 * echelle, 11 * echelle, 8),
    cercle(ROUGE, 9, cx + 6 * echelle, yFeuillage + 44 * echelle, 11 * echelle, 8),
  ]
}

/**
 * Le lapin, heros des deux premieres histoires.
 *
 * `regarde` vaut 1 vers la droite et -1 vers la gauche: on ne redessine pas un
 * second lapin miroir a la main, on multiplie les ecarts. Ecrire les deux sens
 * separement est exactement le piege des quatre quarts de tour ecrits a la
 * main, deja paye ailleurs dans ce projet.
 */
function lapin(cx: number, cy: number, regarde: 1 | -1 = 1, ech = 1): Trait[] {
  const d = (v: number): number => Math.round(cx + v * regarde * ech)
  const y = (v: number): number => Math.round(cy + v * ech)
  return [
    // Le corps et la tete.
    cercle(NOIR, 11, cx, y(0), 62 * ech, 14),
    cercle(NOIR, 11, d(58), y(-62), 42 * ech, 14),
    // Les deux oreilles, et le DEDANS rose de la premiere: c'est ce petit
    // detail qui fait un lapin plutot qu'un chat.
    t(NOIR, 10, d(46), y(-96), d(38), y(-176)),
    t(NOIR, 10, d(70), y(-98), d(74), y(-178)),
    t(ROSE, 6, d(48), y(-112), d(43), y(-164)),
    // L'oeil et le museau.
    cercle(NOIR, 8, d(70), y(-70), 6 * ech, 8),
    t(ROSE, 9, d(96), y(-52), d(88), y(-44)),
    // Deux moustaches.
    t(NOIR, 5, d(88), y(-48), d(126), y(-54)),
    t(NOIR, 5, d(88), y(-42), d(124), y(-32)),
    // La queue en pompon, a l'arriere.
    cercle(BLANC_VOILE, 9, d(-58), y(10), 16 * ech, 8),
    // Les pattes.
    t(NOIR, 10, d(-24), y(58), d(-30), y(96)),
    t(NOIR, 10, d(30), y(58), d(36), y(96)),
  ]
}

function maison(cx: number, ech = 1): Trait[] {
  const l = 150 * ech
  const h = 140 * ech
  const y0 = HAUTEUR_SOL
  const fx = Math.round(cx + 82 * ech)
  const fy = Math.round(y0 - 88 * ech)
  const fr = Math.round(28 * ech)
  return [
    t(BRUN, 14, cx - l, y0, cx - l, y0 - h, cx + l, y0 - h, cx + l, y0),
    t(ROUGE, 16, cx - l - 24, y0 - h, cx, y0 - h - 110 * ech, cx + l + 24, y0 - h),
    t(BRUN, 12, cx - 40 * ech, y0, cx - 40 * ech, y0 - 90 * ech, cx + 40 * ech, y0 - 90 * ech, cx + 40 * ech, y0),
    // La poignee de la porte.
    cercle(JAUNE, 7, cx + 26 * ech, y0 - 42 * ech, 6 * ech, 8),
    // Une fenetre a croisillons, avec sa lumiere.
    t(BLEU_PALE, 8, fx - fr, fy - fr, fx + fr, fy - fr, fx + fr, fy + fr, fx - fr, fy + fr, fx - fr, fy - fr),
    t(BLEU_PALE, 6, fx, fy - fr, fx, fy + fr),
    t(BLEU_PALE, 6, fx - fr, fy, fx + fr, fy),
    // La cheminee et sa fumee en trois ronds.
    t(BRUN, 12, cx + 88 * ech, y0 - h - 32 * ech, cx + 88 * ech, y0 - h - 86 * ech),
    cercle(GRIS, 7, cx + 96 * ech, y0 - h - 110 * ech, 14 * ech, 8),
    cercle(GRIS, 7, cx + 112 * ech, y0 - h - 140 * ech, 17 * ech, 8),
  ]
}

function etoiles(): Trait[] {
  const s: Trait[] = []
  const points: [number, number][] = [
    [140, 150],
    [320, 90],
    [520, 180],
    [700, 110],
    [880, 200],
    [230, 260],
  ]
  for (const [x, y] of points) {
    s.push(t(JAUNE, 8, x - 18, y, x + 18, y), t(JAUNE, 8, x, y - 18, x, y + 18))
  }
  return s
}

function barque(cx: number, cy: number): Trait[] {
  return [
    t(BRUN, 16, cx - 130, cy, cx - 96, cy + 48, cx + 96, cy + 48, cx + 130, cy),
    t(BRUN, 12, cx, cy, cx, cy - 130),
    t(BLANC_VOILE, 12, cx, cy - 130, cx + 92, cy - 20, cx, cy - 20),
  ]
}

function vagues(cy: number): Trait[] {
  const v: Trait[] = []
  for (let i = 0; i < 3; i++) {
    const y = cy + i * 42
    v.push(t(BLEU, 12, 60, y, 220, y - 22, 380, y, 540, y - 22, 700, y, 860, y - 22, 960, y))
  }
  return v
}

function poisson(cx: number, cy: number, regarde: 1 | -1 = 1): Trait[] {
  const d = (v: number): number => Math.round(cx + v * regarde)
  return [
    t(JAUNE, 11, d(-46), cy, d(0), cy - 30, d(52), cy, d(0), cy + 30, d(-46), cy),
    t(JAUNE, 11, d(-46), cy, d(-84), cy - 28, d(-84), cy + 28, d(-46), cy),
    cercle(NOIR, 7, d(24), cy - 8, 5, 8),
  ]
}

const ORANGE = 0xef8f3f

/**
 * Le renard, heros de la quatrieme histoire. Meme convention que le lapin:
 * `regarde` multiplie les ecarts, on ne dessine jamais deux renards miroirs.
 */
function renard(cx: number, cy: number, regarde: 1 | -1 = 1, ech = 1): Trait[] {
  const d = (v: number): number => Math.round(cx + v * regarde * ech)
  const y = (v: number): number => Math.round(cy + v * ech)
  return [
    // Le corps allonge et la tete.
    cercle(ORANGE, 11, cx, y(0), 66 * ech, 14),
    cercle(ORANGE, 11, d(72), y(-52), 40 * ech, 14),
    // Le museau pointu, et sa truffe.
    t(ORANGE, 10, d(100), y(-44), d(138), y(-28)),
    cercle(NOIR, 8, d(138), y(-28), 6 * ech, 6),
    // Les deux oreilles en triangle.
    t(ORANGE, 10, d(48), y(-84), d(40), y(-132), d(66), y(-92)),
    t(ORANGE, 10, d(78), y(-88), d(86), y(-136), d(98), y(-84)),
    // L'oeil.
    cercle(NOIR, 8, d(82), y(-58), 6 * ech, 8),
    // La grande queue, avec son bout blanc.
    t(ORANGE, 16, d(-58), y(6), d(-116), y(-30)),
    cercle(BLANC_VOILE, 10, d(-124), y(-38), 16 * ech, 8),
    // Les pattes.
    t(NOIR, 10, d(-26), y(60), d(-30), y(98)),
    t(NOIR, 10, d(34), y(60), d(38), y(98)),
  ]
}

/** Le papillon: un corps, quatre ailes rondes, deux antennes. */
function papillon(cx: number, cy: number, ech = 1): Trait[] {
  const y = (v: number): number => Math.round(cy + v * ech)
  const x = (v: number): number => Math.round(cx + v * ech)
  return [
    t(NOIR, 9, cx, y(-34), cx, y(34)),
    t(NOIR, 6, cx, y(-34), x(-16), y(-58)),
    t(NOIR, 6, cx, y(-34), x(16), y(-58)),
    cercle(ROSE, 10, x(-34), y(-16), 26 * ech, 10),
    cercle(ROSE, 10, x(34), y(-16), 26 * ech, 10),
    cercle(JAUNE, 10, x(-30), y(20), 20 * ech, 10),
    cercle(JAUNE, 10, x(30), y(20), 20 * ech, 10),
  ]
}

/** Deux montagnes au fond, chapeautees de neige. */
function montagnes(): Trait[] {
  return [
    t(GRIS, 14, 60, HAUTEUR_SOL, 320, 260, 580, HAUTEUR_SOL),
    t(GRIS, 14, 440, HAUTEUR_SOL, 720, 340, 960, HAUTEUR_SOL),
    t(BLANC_VOILE, 12, 250, 330, 320, 260, 390, 330),
    t(BLANC_VOILE, 12, 650, 410, 720, 340, 790, 410),
  ]
}

/** La neige qui tombe: des petites croix, jamais deux sur la meme ligne. */
function neige(): Trait[] {
  const flocons: Trait[] = []
  const points: [number, number][] = [
    [150, 180],
    [340, 120],
    [520, 220],
    [700, 140],
    [860, 240],
    [240, 340],
    [620, 380],
  ]
  for (const [x, y] of points) {
    flocons.push(t(BLANC_VOILE, 6, x - 12, y, x + 12, y), t(BLANC_VOILE, 6, x, y - 12, x, y + 12))
  }
  return flocons
}

/** Le bonhomme de neige: trois boules, la carotte, le chapeau, les bras. */
function bonhommeNeige(cx: number, ech = 1): Trait[] {
  const y0 = HAUTEUR_SOL
  const y = (v: number): number => Math.round(y0 - v * ech)
  const x = (v: number): number => Math.round(cx + v * ech)
  return [
    cercle(BLANC_VOILE, 12, cx, y(70), 70 * ech, 14),
    cercle(BLANC_VOILE, 12, cx, y(175), 52 * ech, 14),
    cercle(BLANC_VOILE, 12, cx, y(255), 36 * ech, 14),
    // Les yeux et la carotte.
    cercle(NOIR, 7, x(-12), y(264), 5 * ech, 6),
    cercle(NOIR, 7, x(12), y(264), 5 * ech, 6),
    t(ORANGE, 9, cx, y(250), x(34), y(242)),
    // Le chapeau.
    t(NOIR, 10, x(-34), y(288), x(34), y(288)),
    t(NOIR, 14, x(-20), y(310), x(20), y(310)),
    // Les bras en branche.
    t(BRUN, 8, x(-48), y(180), x(-96), y(215)),
    t(BRUN, 8, x(48), y(180), x(96), y(215)),
    // Trois boutons.
    cercle(NOIR, 6, cx, y(160), 5 * ech, 6),
    cercle(NOIR, 6, cx, y(185), 5 * ech, 6),
    cercle(NOIR, 6, cx, y(135), 5 * ech, 6),
  ]
}

/** Le feu de camp: les buches croisees et trois flammes. */
function feuCamp(cx: number): Trait[] {
  const y0 = HAUTEUR_SOL
  return [
    t(BRUN, 14, cx - 70, y0, cx + 70, y0 - 30),
    t(BRUN, 14, cx - 70, y0 - 30, cx + 70, y0),
    t(ROUGE, 12, cx - 30, y0 - 20, cx - 10, y0 - 90, cx + 6, y0 - 30),
    t(ORANGE, 11, cx + 4, y0 - 24, cx + 22, y0 - 110, cx + 36, y0 - 32),
    t(JAUNE, 10, cx - 12, y0 - 26, cx, y0 - 140, cx + 12, y0 - 28),
  ]
}

/** L'etoile filante: une etoile et sa trainee. */
function etoileFilante(cx: number, cy: number): Trait[] {
  return [
    t(JAUNE, 9, cx - 20, cy, cx + 20, cy),
    t(JAUNE, 9, cx, cy - 20, cx, cy + 20),
    t(JAUNE, 9, cx - 14, cy - 14, cx + 14, cy + 14),
    t(JAUNE, 9, cx - 14, cy + 14, cx + 14, cy - 14),
    t(BLANC_VOILE, 7, cx + 30, cy - 10, cx + 150, cy - 50),
    t(BLANC_VOILE, 7, cx + 34, cy + 8, cx + 170, cy - 18),
  ]
}

function fleurs(): Trait[] {
  const f: Trait[] = []
  for (const [x, c] of [
    [180, ROUGE],
    [420, JAUNE],
    [660, ROSE],
  ] as [number, number][]) {
    f.push(t(VERT_FONCE, 9, x, HAUTEUR_SOL, x, HAUTEUR_SOL - 70))
    f.push(cercle(c, 10, x, HAUTEUR_SOL - 88, 26, 10))
  }
  return f
}

// ------------------------------------------------------------------ modele

/** Une case: son dessin et sa phrase. */
export interface CaseBD {
  /** Le dessin de la case, au format du musee. */
  traits: Trait[]
  /** Le texte, en francais accentue. C'est LUI qu'on lit. */
  texte: string
}

/** Une bande dessinee: un titre, un auteur, et ses cases dans l'ordre. */
export interface BandeDessinee {
  /** Identifiant stable, sans accent: sert de clef et de ligne de journal. */
  id: string
  titre: string
  /** L'auteur affiche. Ce sont des histoires de la maison, pas des emprunts. */
  auteur: string
  /** Pictogramme de couverture. */
  icone: string
  /** Une phrase de presentation, pour choisir dans le rayon. */
  resume: string
  cases: CaseBD[]
}

// ---------------------------------------------------------------- le rayon

/**
 * Le rayon de la bibliotheque.
 *
 * SIX histoires de quatre cases: assez pour qu'il y ait un vrai choix a faire,
 * assez court pour qu'on aille chaque fois jusqu'au bout. Les phrases restent
 * au present et a la troisieme personne, sans subordonnee — c'est le registre
 * de la premiere et de la deuxieme annee, celui que l'ecole du jeu commence
 * par enseigner.
 */
export const BANDES_DESSINEES: readonly BandeDessinee[] = Object.freeze([
  {
    id: 'lapin-sous-la-pluie',
    titre: 'Le lapin sous la pluie',
    auteur: 'La bibliothèque du village',
    icone: '🐇',
    resume: 'Il pleut sur la forêt. Où le lapin va-t-il s’abriter ?',
    cases: [
      {
        traits: [...sol(), ...soleil(), ...arbre(300), ...arbre(760, 0.8), ...lapin(520, 660, 1)],
        texte: 'Le soleil brille sur la forêt. Le lapin grignote une feuille.',
      },
      {
        traits: [...sol(), ...nuage(400, 200), ...nuage(720, 240, 0.8), ...pluie(400, 260), ...lapin(520, 660, -1)],
        texte: 'Un gros nuage arrive. Les premières gouttes tombent.',
      },
      {
        traits: [...sol(), ...nuage(300, 190), ...pluie(300, 250), ...arbre(700), ...lapin(660, 690, 1, 0.9)],
        texte: 'Vite ! Le lapin court se cacher sous le grand arbre.',
      },
      {
        traits: [...sol(), ...soleil(200, 180), ...arbre(700), ...fleurs(), ...lapin(420, 660, 1)],
        texte: 'La pluie s’arrête. La forêt sent bon et les fleurs sont ouvertes.',
      },
    ],
  },
  {
    id: 'la-maison-du-lapin',
    titre: 'La maison du lapin',
    auteur: 'La bibliothèque du village',
    icone: '🏠',
    resume: 'Le lapin cherche un ami pour ne plus dormir tout seul.',
    cases: [
      {
        traits: [...sol(), ...maison(520), ...soleil(), ...lapin(220, 680, 1, 0.9)],
        texte: 'Le lapin passe devant une petite maison. La porte est ouverte.',
      },
      {
        traits: [...sol(), ...maison(520), ...lapin(430, 690, 1, 0.85), ...fleurs()],
        texte: 'Il apporte trois fleurs. C’est son cadeau pour dire bonjour.',
      },
      {
        traits: [...sol(), ...maison(520), ...lapin(360, 690, 1, 0.8), ...lapin(680, 690, -1, 0.8)],
        texte: 'Un autre lapin sort de la maison. Ils deviennent amis.',
      },
      {
        traits: [...sol(0x4f7d3a), ...maison(520), ...etoiles(), ...lapin(520, 700, 1, 0.7)],
        texte: 'La nuit tombe. Le lapin dort au chaud, et il n’est plus seul.',
      },
    ],
  },
  {
    id: 'la-barque-et-le-poisson',
    titre: 'La barque et le poisson',
    auteur: 'La bibliothèque du village',
    icone: '🛶',
    resume: 'Une barque part au large. Que ramène-t-elle ?',
    cases: [
      {
        traits: [...vagues(700), ...soleil(), ...barque(500, 620)],
        texte: 'La barque quitte la plage. La mer est calme ce matin.',
      },
      {
        traits: [...vagues(700), ...barque(400, 620), ...poisson(720, 820, -1)],
        texte: 'Sous l’eau, un poisson jaune suit la barque.',
      },
      {
        traits: [...vagues(700), ...nuage(220, 180), ...barque(560, 620), ...poisson(300, 840, 1)],
        texte: 'Le vent se lève. Il faut rentrer avant le gros nuage.',
      },
      {
        traits: [...sol(0xe8d9a8), ...soleil(180, 170), ...barque(560, 700), ...poisson(300, 690, -1)],
        texte: 'De retour au port ! Le poisson est relâché : on ne garde rien.',
      },
    ],
  },
  {
    id: 'le-renard-et-le-papillon',
    titre: 'Le renard et le papillon',
    auteur: 'La bibliothèque du village',
    icone: '🦊',
    resume: 'Un renard veut attraper un papillon. Il va apprendre autre chose.',
    cases: [
      {
        traits: [...sol(), ...soleil(), ...arbre(220, 0.9), ...renard(560, 660, 1), ...papillon(800, 480)],
        texte: 'Un papillon danse dans le pré. Le renard le regarde.',
      },
      {
        traits: [...sol(), ...arbre(180, 0.8), ...renard(500, 660, 1, 0.95), ...papillon(700, 340)],
        texte: 'Le renard saute très haut. Le papillon monte encore plus haut !',
      },
      {
        traits: [...sol(), ...nuage(760, 180, 0.8), ...renard(480, 680, 1, 0.9), ...fleurs()],
        texte: 'Le renard est fatigué. Il se couche dans les fleurs.',
      },
      {
        traits: [...sol(), ...soleil(160, 170), ...renard(480, 680, 1, 0.9), ...papillon(560, 470, 0.9)],
        texte: 'Le papillon se pose sur son nez. Ils deviennent amis.',
      },
    ],
  },
  {
    id: 'la-nuit-des-etoiles',
    titre: 'La nuit des étoiles',
    auteur: 'La bibliothèque du village',
    icone: '🌠',
    resume: 'Le lapin et le renard veillent près du feu. Le ciel prépare une surprise.',
    cases: [
      {
        traits: [...sol(0x4f7d3a), ...feuCamp(500), ...lapin(260, 690, 1, 0.85), ...renard(760, 690, -1, 0.85)],
        texte: 'La nuit tombe. Le lapin et le renard allument un feu de camp.',
      },
      {
        traits: [...sol(0x4f7d3a), ...etoiles(), ...feuCamp(500), ...lapin(260, 690, 1, 0.85)],
        texte: 'Les étoiles s’allument une à une. On ne compte plus.',
      },
      {
        traits: [...sol(0x4f7d3a), ...etoiles(), ...etoileFilante(420, 220), ...feuCamp(500), ...renard(760, 690, -1, 0.85)],
        texte: 'Une étoile filante traverse le ciel ! Vite, un vœu !',
      },
      {
        traits: [...sol(0x4f7d3a), ...etoiles(), ...feuCamp(500), ...lapin(340, 700, 1, 0.8), ...renard(680, 700, -1, 0.8)],
        texte: 'Les deux amis ont fait le même vœu : rester amis pour toujours.',
      },
    ],
  },
  {
    id: 'le-bonhomme-de-neige',
    titre: 'Le bonhomme de neige',
    auteur: 'La bibliothèque du village',
    icone: '⛄',
    resume: 'Il neige sur la montagne. Le lapin a une idée toute blanche.',
    cases: [
      {
        traits: [...montagnes(), ...sol(0xe8f2f8), ...neige(), ...lapin(500, 680, 1, 0.9)],
        texte: 'Il neige sur la montagne. Le lapin attrape les flocons.',
      },
      {
        traits: [...montagnes(), ...sol(0xe8f2f8), ...lapin(300, 690, 1, 0.85), ...bonhommeNeige(620, 0.6)],
        texte: 'Il roule une boule, puis deux, puis trois. Ça monte !',
      },
      {
        traits: [...montagnes(), ...sol(0xe8f2f8), ...neige(), ...lapin(300, 690, 1, 0.85), ...bonhommeNeige(620, 1)],
        texte: 'Une carotte pour le nez, un chapeau pour la tête. Bonjour, monsieur !',
      },
      {
        traits: [...montagnes(), ...sol(0xe8f2f8), ...soleil(160, 160), ...lapin(300, 690, 1, 0.85), ...bonhommeNeige(620, 1)],
        texte: 'Le soleil se lève sur la montagne. Le bonhomme de neige sourit.',
      },
    ],
  },
])

/** Une bande dessinee par son identifiant, ou null. */
export function bandeDessinee(id: string): BandeDessinee | null {
  return BANDES_DESSINEES.find((b) => b.id === id) ?? null
}
